import React, { type FC, memo, type ReactNode, use, useCallback, useEffect, useMemo, useState } from "react";
import { GQL } from "@binale-tech/shared";
import { gql, type TypedDocumentNode } from "@apollo/client";
import { Controller, useForm } from "react-hook-form";

import { Alert, Button, Drawer, type Key, Label, ListBox, Select, Spinner, Switch, Table } from "@heroui/react";

import { LoadingOverlay } from "@ui-components/LoadingOverlay";
import { UserContext } from "@domain/context/UserProvider";
import { BuContext } from "@domain/context/BuContext";
import { CompanyContext, CompanyControlContext, YearPeriodContext } from "@domain/context/CompanyContext";
import { BuTaxesSKR } from "@domain/models/BuTaxUtils";
import { FormattedMessage, useIntl } from "react-intl";

import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { useToastSaver } from "@ui-components/hooks/useToastSaver";
import { useMutation, useQuery } from "@apollo/client/react";
import dayjs from "dayjs";
import { CompanySaldoDrawer } from "./CompanySaldoDrawer";
import { PopconfirmHero } from "@ui-components/PopconfirmHero";
import { parseDate } from "@internationalized/date";
import { DateInput } from "@ui-components/Inputs/DateInput";

const fragmentCompany = gql`
    fragment CompanyForAccountingYearCreation on Company {
        id
        name
        accountingYears
        legalName
        legalForm
        products {
            id
            productKey
        }
        accountingConfigs {
            id
            year
            kontoExt
            taxation
            skr
            fiscalYearStart
            ustvaFrequency
            dauerfristverlaengerung
            steuerlichBeraten
        }
    }
`;
const query: TypedDocumentNode<Pick<GQL.IQuery, "company">, GQL.IQueryCompanyArgs> = gql`
    ${fragmentCompany}
    query getCompanyForEdit($id: ID!) {
        company(id: $id) {
            ...CompanyForAccountingYearCreation
        }
    }
`;

const mutationCreate: TypedDocumentNode<
    Pick<GQL.IMutation, "companyCreateAccountingYear">,
    GQL.IMutationCompanyCreateAccountingYearArgs
> = gql`
    ${fragmentCompany}
    mutation companyCreateAccountingYear($input: CompanyCreateAccountingYearInput!) {
        companyCreateAccountingYear(input: $input) {
            ...CompanyForAccountingYearCreation
        }
    }
`;
const mutationCopyAccounts: TypedDocumentNode<
    Pick<GQL.IMutation, "companyCopyNextYearAccounts">,
    GQL.IMutationCompanyCopyNextYearAccountsArgs
> = gql`
    ${fragmentCompany}
    mutation companyCopyNextYearAccounts($input: CompanyCopyNextYearAccountsInput!) {
        companyCopyNextYearAccounts(input: $input) {
            ...CompanyForAccountingYearCreation
        }
    }
`;
// SP2 (FIBU-22): filing-config editing (§II.7) - a small settings toggle per year row,
// not part of the create/copy form; each onChange below fires this mutation directly.
const mutationFilingConfigSave: TypedDocumentNode<
    Pick<GQL.IMutation, "companyAccountingYearFilingConfigSave">,
    GQL.IMutationCompanyAccountingYearFilingConfigSaveArgs
> = gql`
    ${fragmentCompany}
    mutation companyAccountingYearFilingConfigSave($input: CompanyAccountingYearFilingConfigInput!) {
        companyAccountingYearFilingConfigSave(input: $input) {
            ...CompanyForAccountingYearCreation
        }
    }
`;

const CONFIRM_TITLE = <FormattedMessage id="app.confirmation.header" />;
const KONTO_LEN_LABEL = <FormattedMessage id="app.fields.konto_len" />;
const TAXATION_LABEL = <FormattedMessage id="app.fields.taxation" />;

// Wrapper to make HeroUI Select work with Ant Design Form
const FormSelectFC: FC<{
    value?: string | number | null;
    onChange?: (value: string | number | null) => void;
    disabled?: boolean;
    allowClear?: boolean;
    label?: ReactNode;
    isRequired?: boolean;
    options: { value: string | number | null; label: ReactNode; disabled?: boolean }[];
}> = ({ value, onChange, disabled, options, allowClear, label, isRequired }) => {
    const disabledKeys = useMemo(
        () => options.filter((o) => o.disabled).map((o) => (o.value === null ? "null" : String(o.value))),
        [options],
    );
    const handleChange = useCallback(
        (key: Key | null) => {
            const selected = key as string;
            if (!selected && allowClear) {
                onChange?.(null);
            } else {
                const numValue = Number(selected);
                onChange?.(isNaN(numValue) ? selected : numValue);
            }
        },
        [allowClear, onChange],
    );

    return (
        <Select
            className="w-full"
            isRequired={isRequired}
            value={value !== null && value !== undefined ? String(value) : ""}
            disabledKeys={disabledKeys}
            onChange={handleChange}
            isDisabled={disabled}
        >
            {label ? <Label>{label}</Label> : null}
            <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
                <ListBox>
                    {options.map((option) => (
                        <ListBox.Item
                            key={option.value === null ? "null" : String(option.value)}
                            id={option.value === null ? "null" : String(option.value)}
                            textValue={String(option.label)}
                        >
                            <Label>{option.label}</Label>
                            <ListBox.ItemIndicator />
                        </ListBox.Item>
                    ))}
                </ListBox>
            </Select.Popover>
        </Select>
    );
};
const FormSelect = memo(FormSelectFC);

// Wrapper to make HeroUI Select with groups work with Ant Design Form
const FormSelectGroupedFC: FC<{
    value?: number | null;
    onChange?: (value: number | null) => void;
    disabled?: boolean;
    label?: ReactNode;
    isRequired?: boolean;
    options: { label: string; options: { value: number; label: ReactNode; disabled?: boolean }[] }[];
}> = ({ value, onChange, disabled, options, label, isRequired }) => {
    const disabledKeys = useMemo(
        () =>
            options
                .flatMap((group) => group.options)
                .filter((o) => o.disabled)
                .map((o) => String(o.value)),
        [options],
    );
    const handleChange = useCallback(
        (key: Key | null) => {
            const selected = key as string;
            onChange?.(selected ? Number(selected) : null);
        },
        [onChange],
    );

    return (
        <Select
            className="w-full"
            isRequired={isRequired}
            value={value !== null && value !== undefined ? String(value) : ""}
            disabledKeys={disabledKeys}
            onChange={handleChange}
            isDisabled={disabled}
        >
            {label ? <Label>{label}</Label> : null}
            <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
                <ListBox>
                    {options.map((group) => (
                        <ListBox.Section key={group.label}>
                            <Label slot="header">{group.label}</Label>
                            {group.options.map((option) => (
                                <ListBox.Item
                                    key={String(option.value)}
                                    id={String(option.value)}
                                    textValue={String(option.label)}
                                >
                                    <Label>{option.label}</Label>
                                    <ListBox.ItemIndicator />
                                </ListBox.Item>
                            ))}
                        </ListBox.Section>
                    ))}
                </ListBox>
            </Select.Popover>
        </Select>
    );
};
const FormSelectGrouped = memo(FormSelectGroupedFC);

type Props = {
    onHide?: () => void;
    companyId: string;
    /** The company with members, which the year form query does not carry; permission checks read it. */
    accessCompany: GQL.ICompany | undefined;
};
const dateFormat = "YYYY-MM-DD";
const CompanyYearFormDrawerFC: FC<Props> = ({ companyId, onHide, accessCompany }) => {
    const intl = useIntl();
    const user = use(UserContext);
    const canWriteYears = ProductAccessUtils.canWriteAny(
        ProductAccessUtils.getAccountingProductKeyList(),
        accessCompany,
        user,
    );
    const { control, handleSubmit, watch, reset, setValue } = useForm<GQL.ICompanyCreateAccountingYearInput>();
    const toastSaver = useToastSaver();
    const { companyGQL: activeCompany } = use(CompanyContext);
    const { onChangeYear } = use(YearPeriodContext);
    const { triggerDataRefetch } = use(CompanyControlContext);

    const { allBuTimeframes } = use(BuContext);
    const [saldoTransferYearTo, setSaldoTransferYearTo] = useState<number>();
    useEffect(() => {
        if (saldoTransferYearTo) {
            onChangeYear(saldoTransferYearTo);
        }
    }, [onChangeYear, saldoTransferYearTo]);

    const {
        data,
        loading: queryLoading,
        refetch,
    } = useQuery(query, {
        variables: { id: companyId },
        skip: !companyId,
        fetchPolicy: "no-cache",
    });
    const company = data?.company;

    const [createAccountingYear, { loading: createLoading }] = useMutation(mutationCreate);

    const [copyAccounts, { loading: copyLoading }] = useMutation(mutationCopyAccounts);

    // SP2 (FIBU-22) - filing-config cells (§II.7): the mutation always fully replaces
    // all three fields (no partial-merge semantics, see the backend resolver comment),
    // so every handler below sends the row's current values with just its own field
    // overridden. `query` is `no-cache`, so the row is refreshed via an explicit
    // `refetch()` after each save.
    const [saveFilingConfig] = useMutation(mutationFilingConfigSave);
    const handleFilingConfigChange = useCallback(
        (
            yc: GQL.ICompanyAccountingYear,
            patch: Partial<
                Pick<
                    GQL.ICompanyAccountingYearFilingConfigInput,
                    "ustvaFrequency" | "dauerfristverlaengerung" | "steuerlichBeraten"
                >
            >,
        ) => {
            toastSaver(() =>
                saveFilingConfig({
                    variables: {
                        input: {
                            companyId,
                            year: yc.year,
                            ustvaFrequency: yc.ustvaFrequency,
                            dauerfristverlaengerung: yc.dauerfristverlaengerung,
                            steuerlichBeraten: yc.steuerlichBeraten,
                            ...patch,
                        },
                    },
                }),
            ).then(() => {
                refetch();
                // Sibling flows (onSubmit, makeCopyHandler below) also call triggerDataRefetch()
                // so CompanyContext/yearConfig picks up the change - without it, the drawer's own
                // `refetch()` refreshes only this drawer's local `query`, and the home widget /
                // /calendar page (which read yearConfig via CompanyAccountingYearContext) keep
                // showing the stale ustvaFrequency/DFV/steuerlichBeraten until a hard reload.
                triggerDataRefetch();
            });
        },
        [companyId, saveFilingConfig, toastSaver, refetch, triggerDataRefetch],
    );
    const makeUstvaFrequencyHandler = useCallback(
        (yc: GQL.ICompanyAccountingYear) => (value: string | number | null) =>
            handleFilingConfigChange(yc, {
                ustvaFrequency: value as GQL.IUstvaFilingFrequency,
                // the server refuses Keine UStVA with a Dauerfristverlängerung
                ...(value === GQL.IUstvaFilingFrequency.None ? { dauerfristverlaengerung: false } : {}),
            }),
        [handleFilingConfigChange],
    );
    const makeDauerfristverlaengerungHandler = useCallback(
        (yc: GQL.ICompanyAccountingYear) => (value: boolean) =>
            handleFilingConfigChange(yc, { dauerfristverlaengerung: value }),
        [handleFilingConfigChange],
    );
    const makeSteuerlichBeratenHandler = useCallback(
        (yc: GQL.ICompanyAccountingYear) => (value: string | number | null) =>
            handleFilingConfigChange(yc, {
                steuerlichBeraten: value === "TRUE" ? true : value === "FALSE" ? false : null,
            }),
        [handleFilingConfigChange],
    );

    const loading = queryLoading || createLoading || copyLoading;

    const year = watch("year");
    const fiscalYearStartMinValue = useMemo(() => (year ? parseDate(`${year}-01-01`) : undefined), [year]);
    const fiscalYearStartMaxValue = useMemo(() => (year ? parseDate(`${year}-12-31`) : undefined), [year]);
    const hasSKR3 = BuTaxesSKR.hasBuTimeframeCategoriesByYear(3, year, allBuTimeframes);
    const hasSKR4 = BuTaxesSKR.hasBuTimeframeCategoriesByYear(4, year, allBuTimeframes);
    const hasSKR = hasSKR3 && hasSKR4;

    const hasAccounting = Boolean(company?.id) && ProductAccessUtils.hasCompanyAccountingProducts(company);
    const accountingYears = company?.accountingYears || [];
    const [existingYearMin, existingYearMax] = [Math.min(...accountingYears), Math.max(...accountingYears)];
    const accountingYearsNew = accountingYears.length
        ? Array.from(new Set([existingYearMin - 1, existingYearMax + 1]))
        : [...Array(new Date().getFullYear() + 1 - 2015 + 1).keys()].map((v) => v + 2015);

    useEffect(() => {
        reset();
    }, [reset, company]);

    useEffect(() => {
        if (year) {
            setValue("fiscalYearStart", (year + "-01-01") as any);
        }
    }, [setValue, year]);

    const onSubmit = (values: GQL.ICompanyCreateAccountingYearInput) => {
        toastSaver(() =>
            createAccountingYear({
                variables: {
                    input: { ...values, companyId, fiscalYearStart: dayjs(values.fiscalYearStart).format(dateFormat) },
                },
            }),
        ).then(() => {
            triggerDataRefetch();
            reset();
            onHide();
        });
    };

    const makeCopyHandler = useCallback(
        (copyYear: number) => () => {
            toastSaver(() =>
                copyAccounts({
                    variables: {
                        input: { year: copyYear, companyId },
                    },
                }),
            ).then(() => triggerDataRefetch());
        },
        [toastSaver, copyAccounts, companyId, triggerDataRefetch],
    );
    const makeTransferHandler = useCallback((transferYear: number) => () => setSaldoTransferYearTo(transferYear), []);
    const makeCopyTrigger = useCallback(
        (copyYear: number) => (
            <Button variant="ghost" size="sm" isDisabled={!canWriteYears}>
                <FormattedMessage id="app.titles.copy_data_from" /> {copyYear + 1}
            </Button>
        ),
        [canWriteYears],
    );

    const copyDataFromYearInfo =
        accountingYears.length === 0 || !year ? undefined : year > existingYearMax ? existingYearMax : existingYearMin;

    const formYear = watch("year");

    const getLenMinMax = (companyGql: GQL.ICompany, selectedYear: number): [number, number] => {
        if (!companyGql || !selectedYear) {
            return [0, 2];
        }
        if (!company?.accountingConfigs?.length) {
            return [0, 2];
        }
        const yMin = Math.min(...companyGql.accountingYears);
        const kontoExtMinYear = company.accountingConfigs.find((v) => v.year === yMin).kontoExt;
        if (formYear < yMin) {
            return [kontoExtMinYear, kontoExtMinYear];
        }
        const yMax = Math.max(...companyGql.accountingYears);
        const kontoExtMaxYear = company.accountingConfigs.find((v) => v.year === yMax).kontoExt;
        return [kontoExtMaxYear, 2];
    };
    const [kontoExtMin, kontoExtMax] = getLenMinMax(company, formYear);
    const kontoExtAllowedValues = [...Array(kontoExtMax - kontoExtMin + 1).keys()].map((v) => v + kontoExtMin);

    const accountingYearConfigs = (company?.accountingConfigs || []).toSorted((a, b) => b.year - a.year);
    const skrWhitelist = accountingYears.length ? (company?.accountingConfigs || []).map((v) => v.skr).pop() : null;
    const isYearBlockedBySKR = year && !hasSKR;

    const requiredRule = useMemo(
        () => ({ required: intl.formatMessage({ id: "app.validation.error.field_empty" }) }),
        [intl],
    );
    const onOpenChange = useCallback((isOpen: boolean) => !isOpen && onHide(), [onHide]);
    const onSaldoClose = useCallback(() => setSaldoTransferYearTo(undefined), []);
    const yearValues = useMemo(() => ({ year }), [year]);
    const copyYearValues = useMemo(() => ({ year: copyDataFromYearInfo }), [copyDataFromYearInfo]);

    // SP2 (FIBU-22) - filing-config cell option lists (§II.7).
    const ustvaFrequencyOptions = useMemo(
        () => [
            {
                value: GQL.IUstvaFilingFrequency.Monthly,
                label: intl.formatMessage({ id: "app.companies.filingFrequency.monthly" }),
            },
            {
                value: GQL.IUstvaFilingFrequency.Quarterly,
                label: intl.formatMessage({ id: "app.companies.filingFrequency.quarterly" }),
            },
            {
                value: GQL.IUstvaFilingFrequency.None,
                label: intl.formatMessage({ id: "app.companies.filingFrequency.none" }),
            },
        ],
        [intl],
    );
    // Tri-state (unset/true/false): "unset" keeps steuerlichBeraten NULL - see the
    // migration comment on why NULL (not `false`) is the safe default.
    const triStateOptions = useMemo(
        () => [
            { value: "UNSET", label: intl.formatMessage({ id: "app.companies.triState.notSet" }) },
            { value: "TRUE", label: intl.formatMessage({ id: "app.companies.triState.yes" }) },
            { value: "FALSE", label: intl.formatMessage({ id: "app.companies.triState.no" }) },
        ],
        [intl],
    );
    const triStateValue = useCallback(
        (v: boolean | null | undefined): string => (v === true ? "TRUE" : v === false ? "FALSE" : "UNSET"),
        [],
    );

    return (
        <>
            <Drawer.Backdrop isOpen={Boolean(companyId)} onOpenChange={onOpenChange}>
                <Drawer.Content placement="right">
                    <Drawer.Dialog className="w-300 max-w-300">
                        <Drawer.CloseTrigger />
                        <Drawer.Header>
                            <Drawer.Heading>
                                <FormattedMessage id="app.companies.fiscalYearConfig" />
                            </Drawer.Heading>
                        </Drawer.Header>
                        <Drawer.Body>
                            <div className="relative">
                                <LoadingOverlay isLoading={loading} />
                                <div className="flex gap-2 flex-col w-full">
                                    {companyId && !loading && !hasAccounting && (
                                        <Alert status="warning">
                                            <Alert.Content>
                                                <Alert.Description>
                                                    <FormattedMessage id="app.companies.noAccountingProductsWarning" />
                                                </Alert.Description>
                                            </Alert.Content>
                                        </Alert>
                                    )}
                                    <Table>
                                        <Table.ScrollContainer>
                                            <Table.Content
                                                aria-label={intl.formatMessage({
                                                    id: "app.companies.yearsConfigTable",
                                                })}
                                            >
                                                <Table.Header>
                                                    <Table.Column isRowHeader>
                                                        <FormattedMessage id="app.fields.date.year" />
                                                    </Table.Column>
                                                    <Table.Column>
                                                        <FormattedMessage id="app.fields.konto_len" />
                                                    </Table.Column>
                                                    <Table.Column>SKR</Table.Column>
                                                    <Table.Column>SOLL/IST</Table.Column>
                                                    <Table.Column>
                                                        <FormattedMessage id="app.companies.col.fiscalYearStart" />
                                                    </Table.Column>
                                                    <Table.Column>
                                                        <FormattedMessage id="app.companies.col.ustvaFrequency" />
                                                    </Table.Column>
                                                    <Table.Column>
                                                        <FormattedMessage id="app.companies.col.dauerfristverlaengerung" />
                                                    </Table.Column>
                                                    <Table.Column>
                                                        <FormattedMessage id="app.companies.col.steuerlichBeraten" />
                                                    </Table.Column>
                                                    <Table.Column>
                                                        <FormattedMessage id="app.fields.actions" />
                                                    </Table.Column>
                                                    <Table.Column>
                                                        <FormattedMessage id="app.companies.saldoTransfer" />
                                                    </Table.Column>
                                                </Table.Header>
                                                <Table.Body>
                                                    {accountingYearConfigs.map((yearConfig) => (
                                                        <Table.Row key={yearConfig.id}>
                                                            <Table.Cell>{yearConfig.year}</Table.Cell>
                                                            <Table.Cell>{yearConfig.kontoExt + 4}</Table.Cell>
                                                            <Table.Cell>{yearConfig.skr}</Table.Cell>
                                                            <Table.Cell>{yearConfig.taxation}</Table.Cell>
                                                            <Table.Cell>
                                                                {dayjs(yearConfig.fiscalYearStart).format("DD.MM.YYYY")}
                                                            </Table.Cell>
                                                            <Table.Cell>
                                                                <FormSelect
                                                                    value={yearConfig.ustvaFrequency}
                                                                    disabled={!canWriteYears}
                                                                    onChange={makeUstvaFrequencyHandler(yearConfig)}
                                                                    // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop -- memoized options array, re-rendered per row inside an identity-tolerant map body
                                                                    options={ustvaFrequencyOptions}
                                                                />
                                                            </Table.Cell>
                                                            <Table.Cell>
                                                                <Switch
                                                                    isSelected={Boolean(
                                                                        yearConfig.dauerfristverlaengerung,
                                                                    )}
                                                                    isDisabled={
                                                                        !canWriteYears ||
                                                                        yearConfig.ustvaFrequency ===
                                                                            GQL.IUstvaFilingFrequency.None
                                                                    }
                                                                    onChange={makeDauerfristverlaengerungHandler(
                                                                        yearConfig,
                                                                    )}
                                                                >
                                                                    <Switch.Content>
                                                                        <Switch.Control>
                                                                            <Switch.Thumb />
                                                                        </Switch.Control>
                                                                    </Switch.Content>
                                                                </Switch>
                                                            </Table.Cell>
                                                            <Table.Cell>
                                                                <FormSelect
                                                                    value={triStateValue(yearConfig.steuerlichBeraten)}
                                                                    disabled={!canWriteYears}
                                                                    onChange={makeSteuerlichBeratenHandler(yearConfig)}
                                                                    // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop -- memoized options array, re-rendered per row inside an identity-tolerant map body
                                                                    options={triStateOptions}
                                                                />
                                                            </Table.Cell>
                                                            <Table.Cell>
                                                                {yearConfig.year !== existingYearMax ? (
                                                                    <PopconfirmHero
                                                                        onConfirm={makeCopyHandler(yearConfig.year)}
                                                                        title={CONFIRM_TITLE}
                                                                        trigger={makeCopyTrigger(yearConfig.year)}
                                                                    />
                                                                ) : null}
                                                            </Table.Cell>
                                                            <Table.Cell>
                                                                {yearConfig.year !== existingYearMin ? (
                                                                    <Button
                                                                        variant="secondary"
                                                                        size="sm"
                                                                        isDisabled={company?.id !== activeCompany?.id}
                                                                        onPress={makeTransferHandler(yearConfig.year)}
                                                                    >
                                                                        <FormattedMessage id="app.companies.saldoTransfer" />
                                                                    </Button>
                                                                ) : null}
                                                            </Table.Cell>
                                                        </Table.Row>
                                                    ))}
                                                </Table.Body>
                                            </Table.Content>
                                        </Table.ScrollContainer>
                                    </Table>
                                </div>
                            </div>
                            <CompanySaldoDrawer
                                onClose={onSaldoClose}
                                year={saldoTransferYearTo}
                                companyData={company}
                                accessCompany={accessCompany}
                            />
                        </Drawer.Body>
                        <Drawer.Footer>
                            {hasAccounting && (
                                <form onSubmit={handleSubmit(onSubmit)} className="w-full">
                                    <div className="flex gap-2 flex-col w-full">
                                        <div className="flex gap-2 items-end justify-between [&_label]:min-w-25">
                                            <Controller
                                                name="year"
                                                control={control}
                                                rules={requiredRule}
                                                // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- RHF Controller render prop is identity-tolerant; extracting risks controlled-field typing without a tsc gate here
                                                render={({ field }) => (
                                                    <FormSelect
                                                        {...field}
                                                        label={intl.formatMessage({ id: "app.fields.date.year" })}
                                                        isRequired
                                                        allowClear
                                                        // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop -- options derived from a per-render array inside an identity-tolerant RHF render body
                                                        options={accountingYearsNew.map((y) => ({
                                                            label: y,
                                                            value: y,
                                                        }))}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="fiscalYearStart"
                                                control={control}
                                                rules={requiredRule}
                                                // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- RHF Controller render prop is identity-tolerant; extracting risks controlled-field typing without a tsc gate here
                                                render={({ field }) => (
                                                    <DateInput
                                                        {...field}
                                                        label={intl.formatMessage({
                                                            id: "app.companies.col.fiscalYearStart",
                                                        })}
                                                        isRequired
                                                        minValue={fiscalYearStartMinValue}
                                                        maxValue={fiscalYearStartMaxValue}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="kontoExt"
                                                control={control}
                                                rules={requiredRule}
                                                // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- RHF Controller render prop is identity-tolerant; extracting risks controlled-field typing without a tsc gate here
                                                render={({ field }) => (
                                                    <FormSelectGrouped
                                                        {...field}
                                                        label={KONTO_LEN_LABEL}
                                                        isRequired
                                                        disabled={!formYear}
                                                        // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop -- options derived from a per-render array inside an identity-tolerant RHF render body
                                                        options={[
                                                            {
                                                                label: intl.formatMessage({
                                                                    id: "app.companies.kontoLen.standard",
                                                                }),
                                                                options: [
                                                                    {
                                                                        label: 4,
                                                                        value: 0,
                                                                        disabled: !kontoExtAllowedValues.includes(0),
                                                                    },
                                                                ],
                                                            },
                                                            {
                                                                label: intl.formatMessage({
                                                                    id: "app.companies.kontoLen.extended",
                                                                }),
                                                                options: [
                                                                    {
                                                                        label: 5,
                                                                        value: 1,
                                                                        disabled: !kontoExtAllowedValues.includes(1),
                                                                    },
                                                                    {
                                                                        label: 6,
                                                                        value: 2,
                                                                        disabled: !kontoExtAllowedValues.includes(2),
                                                                    },
                                                                ],
                                                            },
                                                        ]}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="skr"
                                                control={control}
                                                rules={requiredRule}
                                                // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- RHF Controller render prop is identity-tolerant; extracting risks controlled-field typing without a tsc gate here
                                                render={({ field }) => (
                                                    <FormSelect
                                                        {...field}
                                                        label="SKR"
                                                        isRequired
                                                        // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop -- static options derived inside an identity-tolerant RHF render body
                                                        options={[
                                                            {
                                                                label: 3,
                                                                value: 3,
                                                                disabled: skrWhitelist && skrWhitelist !== 3,
                                                            },
                                                            {
                                                                label: 4,
                                                                value: 4,
                                                                disabled: skrWhitelist && skrWhitelist !== 4,
                                                            },
                                                        ]}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="taxation"
                                                control={control}
                                                rules={requiredRule}
                                                // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- RHF Controller render prop is identity-tolerant; extracting risks controlled-field typing without a tsc gate here
                                                render={({ field }) => (
                                                    <FormSelect
                                                        {...field}
                                                        label={TAXATION_LABEL}
                                                        isRequired
                                                        // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop -- static options derived inside an identity-tolerant RHF render body
                                                        options={[
                                                            { label: "SOLL", value: GQL.ICompanyTaxation.Soll },
                                                            { label: "IST", value: GQL.ICompanyTaxation.Ist },
                                                            {
                                                                label: intl.formatMessage({
                                                                    id: "app.companies.taxation.none",
                                                                }),
                                                                value: GQL.ICompanyTaxation.Kust,
                                                            },
                                                        ]}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            {isYearBlockedBySKR && (
                                                <Alert status="warning">
                                                    <Alert.Content>
                                                        <Alert.Description>
                                                            <FormattedMessage
                                                                id="app.companies.noSkrForYearWarning"
                                                                values={yearValues}
                                                            />
                                                        </Alert.Description>
                                                    </Alert.Content>
                                                </Alert>
                                            )}
                                            {copyDataFromYearInfo && (
                                                <Alert>
                                                    <Alert.Content>
                                                        <Alert.Description>
                                                            <FormattedMessage
                                                                id="app.companies.dataCopyFromYearInfo"
                                                                values={copyYearValues}
                                                            />
                                                        </Alert.Description>
                                                    </Alert.Content>
                                                </Alert>
                                            )}
                                        </div>
                                        <div className="flex justify-end">
                                            <Button
                                                size="lg"
                                                type="submit"
                                                isPending={loading}
                                                isDisabled={!hasAccounting || isYearBlockedBySKR || !canWriteYears}
                                            >
                                                {({ isPending }) => (
                                                    <>
                                                        {isPending ? <Spinner color="current" size="sm" /> : null}
                                                        🎄&nbsp;
                                                        <FormattedMessage id="app.button.create" />
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </form>
                            )}
                        </Drawer.Footer>
                    </Drawer.Dialog>
                </Drawer.Content>
            </Drawer.Backdrop>
        </>
    );
};

export const CompanyYearFormDrawer = memo(CompanyYearFormDrawerFC);
